import React from 'react';
import { ScrollView, StyleProp, Text, View, ViewStyle } from 'react-native';
import { ColorFunction } from '@styles/colors/types';
import { ScaleFunction } from '@components/operatorPanel/interfaces';
import { font as initialFont } from '@styles/fonts';
import SimpleButton, { SimpleButtonVariant } from '@components/operatorPanel/components/elements/buttons/SimpleButton';
import I18n from 'i18n-js';
import { LinkedNCFile, ProductionBatchStatistics } from '@interfaces/search';
import { useRoute } from '@react-navigation/native';
import { scheduledProductionOperationIdParam } from '@components/operatorPanel/constants/operatorPanel.constants';
import { connect } from 'react-redux';
import { ReducerEntityWithLoadingState } from '@v2/pages/common/interfaces';

export interface NCFileListProps {
  C: ColorFunction;
  scale: ScaleFunction;
  style?: StyleProp<ViewStyle>;
  font?: typeof initialFont;
  ncFiles: LinkedNCFile[];
  onAddNCFile?: () => void;
  ncFile: LinkedNCFile | null;
  setNCFile?: (ncFile: LinkedNCFile) => void;
  showAllButton?: boolean;
  showAllButtonIfNoMain?: boolean;
  withERP?: boolean;
  noMain?: boolean;
  mainNcFileId: number;
  uploadingFiles: any;
  batch: ReducerEntityWithLoadingState<ProductionBatchStatistics | null>;
}

const NCFileList = ({
  C,
  scale,
  style,
  font = initialFont,
  ncFiles,
  onAddNCFile,
  ncFile,
  setNCFile,
  showAllButton,
  showAllButtonIfNoMain,
  withERP,
  mainNcFileId,
  uploadingFiles,
  noMain,
  batch,
}: NCFileListProps) => {
  const route = useRoute();
  const selectedJob = route.params?.[scheduledProductionOperationIdParam];
  const isMachinePerspective = selectedJob == null;
  const shouldHideAddNCFileButton = isMachinePerspective && !!batch?.data?.links && batch?.data?.links?.length === 0;

  if (!withERP) {
    return (
      <View style={[{ flexDirection: 'row' }, style]}>
        <SimpleButton C={C} scale={scale} font={font} variant={SimpleButtonVariant.Fill} text={ncFile?.ncFileName || ''} />
      </View>
    );
  }

  const getButtonStyle = (id: number) => (id === ncFile?.ncFileId ? SimpleButtonVariant.Fill : SimpleButtonVariant.Outline);

  if (noMain) {
    return (
      <View style={[{ flexDirection: 'row', alignItems: 'center', height: scale(26) }, style]}>
        {showAllButtonIfNoMain && ncFiles && ncFiles.length > 1 && (
          <SimpleButton
            C={C}
            scale={scale}
            font={font}
            variant={ncFile === 'all' ? SimpleButtonVariant.Fill : SimpleButtonVariant.Outline}
            text="All"
            style={{ marginRight: scale(8), height: scale(26) }}
            onPress={() => setNCFile?.('all')}
          />
        )}
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={{ flex: 1 }}
          contentContainerStyle={{ flexDirection: 'row', alignItems: 'center' }}
        >
          <>
            {ncFiles.map((f) => (
              <SimpleButton
                key={f.ncFileId}
                C={C}
                scale={scale}
                font={font}
                variant={getButtonStyle(f.ncFileId)}
                text={f.ncFileName}
                onPress={() => setNCFile?.(f)}
                style={{ marginRight: scale(9), height: scale(26) }}
              />
            ))}
          </>

          {uploadingFiles &&
            uploadingFiles.map((f: any) => {
              const uploading = f.status == 'uploading';
              const error = f.status == 'error';
              return (
                <SimpleButton
                  key={f.ncFileId}
                  C={C}
                  scale={scale}
                  font={font}
                  variant={error ? SimpleButtonVariant.Error : getButtonStyle(f.ncFileId)}
                  text={f.ncFileName}
                  onPress={() => (uploading || error ? null : setNCFile?.(f))}
                  style={{ marginRight: scale(9), height: scale(26) }}
                  loading={uploading}
                />
              );
            })}

          {onAddNCFile && !shouldHideAddNCFileButton && (
            <SimpleButton
              style={{ height: scale(26) }}
              C={C}
              scale={scale}
              font={font}
              variant={SimpleButtonVariant.Dashed}
              text="Add NC File"
              onPress={onAddNCFile}
            />
          )}
        </ScrollView>
      </View>
    );
  }

  const otherFiles = ncFiles.filter((f) => f.ncFileId !== mainNcFileId);
  const mainFiles = ncFiles.find((f) => f.ncFileId == mainNcFileId);

  const showOtherSection = otherFiles.length > 0;

  return (
    <View style={[{ flexDirection: 'row', alignItems: 'center', height: scale(26) }, style]}>
      {showAllButton && ncFiles && ncFiles.length > 1 && (
        <SimpleButton
          C={C}
          scale={scale}
          font={font}
          variant={ncFile === 'all' ? SimpleButtonVariant.Fill : SimpleButtonVariant.Outline}
          text="All"
          style={{ marginRight: scale(8), height: scale(26) }}
          onPress={() => setNCFile?.('all')}
        />
      )}

      {mainFiles && (
        <SimpleButton
          C={C}
          scale={scale}
          font={font}
          variant={getButtonStyle(mainFiles.ncFileId)}
          text={mainFiles.ncFileName}
          style={{ marginRight: scale(8), height: scale(26) }}
          onPress={() => setNCFile?.(mainFiles)}
        />
      )}

      {withERP ? (
        <>
          {showOtherSection && (
            <View
              style={{
                width: 1,
                height: '100%',
                backgroundColor: C('Hairline/Light/Transpar [mobile]'),
                marginRight: scale(8),
              }}
            />
          )}
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            style={{ flex: 1 }}
            contentContainerStyle={{ flexDirection: 'row', alignItems: 'center' }}
          >
            {showOtherSection && (
              <>
                <Text style={[font['Subhead 2'], { color: C('Label/Light/Tertiary'), marginRight: scale(8), fontSize: scale(14) }]}>
                  {I18n.t('Other NC Files')}
                </Text>

                {otherFiles.map((f) => (
                  <SimpleButton
                    key={f.ncFileId}
                    C={C}
                    scale={scale}
                    font={font}
                    variant={getButtonStyle(f.ncFileId)}
                    text={f.ncFileName}
                    onPress={() => setNCFile?.(f)}
                    style={{ marginRight: scale(9), height: scale(26) }}
                  />
                ))}
              </>
            )}

            {uploadingFiles &&
              uploadingFiles.map((f: any) => {
                const uploading = f.status == 'uploading';
                const error = f.status == 'error';
                return (
                  <SimpleButton
                    key={f.ncFileId}
                    C={C}
                    scale={scale}
                    font={font}
                    variant={error ? SimpleButtonVariant.Error : getButtonStyle(f.ncFileId)}
                    text={f.ncFileName}
                    onPress={() => (uploading || error ? null : setNCFile?.(f))}
                    style={{ marginRight: scale(9), height: scale(26) }}
                    loading={uploading}
                  />
                );
              })}

            {onAddNCFile && !shouldHideAddNCFileButton && (
              <SimpleButton
                style={{ height: scale(26) }}
                C={C}
                scale={scale}
                font={font}
                variant={SimpleButtonVariant.Dashed}
                text="Add NC File"
                onPress={onAddNCFile}
              />
            )}
          </ScrollView>
        </>
      ) : null}
    </View>
  );
};

export default connect(({ batch: { batch } }) => ({ batch }), {})(NCFileList);
